import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Measures the toolbar container and each button's intrinsic width to decide
 * which buttons must fold into "More".
 *
 * Oscillation-safety: fold decisions are derived from (a) the container's
 * content-box width from ResizeObserver and (b) each button's scrollWidth
 * (intrinsic unconstrained width). We never read offsetWidth or post-fold
 * layout — that would create feedback loops where hiding a button widens the
 * container, which re-triggers measurement, which shows the button again, etc.
 *
 * Active-outranks-idle rule: within the set of buttons that must fold, idle
 * buttons fold before active ones. The caller passes `activeFlags` — a
 * boolean per button in display order — to express which are active. The hook
 * returns `foldedSet: Set<number>` (indices of buttons that must go into More).
 */

const BUTTON_GAP_PX = 8; // gap-2 in Tailwind = 8 px between sibling buttons
// Reserved width for the "More" pill (px-4 text-sm, label up to "More · 9+").
// Over-reserving folds slightly earlier — the safe direction; under-reserving
// would let buttons spill out of the row.
const MORE_BUTTON_ESTIMATE_PX = 110;

export interface ToolbarFoldResult {
    /** Set of button indices (in the caller's array) that should be folded into More. */
    foldedSet: Set<number>;
    /** Ref setter for the button-row container element. */
    containerRef: (el: HTMLDivElement | null) => void;
    /**
     * Stable function to register a button element at a given index.
     * Preferred over a per-index factory to avoid React ref-callback churn
     * (changing a ref callback identity makes React null-then-reattach).
     */
    setButtonEl: (index: number, el: HTMLButtonElement | null) => void;
}

/**
 * @param buttonCount     Number of buttons managed by this hook.
 * @param activeFlags     Per-button active state. Active buttons fold last.
 * @param extraReservedPx Width consumed inside the container by untracked
 *                        siblings (e.g. a fixed icon button + its gap), so the
 *                        fit test doesn't hand their space to the pills.
 */
export function useToolbarFold(
    buttonCount: number,
    activeFlags: readonly boolean[],
    extraReservedPx = 0
): ToolbarFoldResult {
    const containerEl = useRef<HTMLDivElement | null>(null);
    const buttonEls = useRef<Array<HTMLButtonElement | null>>(Array(buttonCount).fill(null));
    // Last-known width per button. Folded buttons unmount (ref becomes null), so
    // without this cache their width would read 0, the totals would shrink, and
    // the fold would oscillate: fold → "fits now" → unfold → doesn't fit → fold…
    const widthCache = useRef<number[]>(Array(buttonCount).fill(0));
    const [foldedSet, setFoldedSet] = useState<Set<number>>(new Set());
    const rafId = useRef<number | null>(null);
    // Keep a stable ref to activeFlags so compute() can read current values
    // without being in the dep array (which would re-schedule on every render).
    const activeFlagsRef = useRef(activeFlags);
    activeFlagsRef.current = activeFlags;
    // Same ref trick for extraReservedPx - keeps compute()'s identity stable.
    const extraReservedPxRef = useRef(extraReservedPx);
    extraReservedPxRef.current = extraReservedPx;

    const compute = useCallback(() => {
        const container = containerEl.current;
        if (!container) return;

        const containerWidth = container.getBoundingClientRect().width;
        if (containerWidth === 0) return;

        // Live-measure mounted buttons; fall back to the cached width for folded
        // (unmounted) ones so totals stay stable across fold states.
        const intrinsicWidths = buttonEls.current.map((el, i) => {
            if (el) {
                const w = el.scrollWidth;
                widthCache.current[i] = w;
                return w;
            }
            return widthCache.current[i] ?? 0;
        });
        const n = intrinsicWidths.length;

        // The More pill is always rendered (some categories permanently live in
        // it), so the fit test must reserve its width too - plus any untracked
        // fixed siblings the caller declared (extraReservedPx).
        const available =
            containerWidth - MORE_BUTTON_ESTIMATE_PX - BUTTON_GAP_PX - extraReservedPxRef.current;

        // Total width if all managed buttons were visible.
        const totalAll =
            intrinsicWidths.reduce((sum, w) => sum + w, 0) + Math.max(0, n - 1) * BUTTON_GAP_PX;

        if (totalAll <= available) {
            setFoldedSet((prev) => (prev.size === 0 ? prev : new Set()));
            return;
        }

        // Build fold candidates ordered by fold-preference:
        //   1. Idle buttons, from the last index toward the first.
        //   2. Active buttons, from the last index toward the first.
        // This ensures active ones survive longest when space is tight.
        const idleIndices: number[] = [];
        const activeIndices: number[] = [];
        for (let i = n - 1; i >= 0; i--) {
            if (activeFlagsRef.current[i]) activeIndices.push(i);
            else idleIndices.push(i);
        }
        const foldOrder = [...idleIndices, ...activeIndices];

        // Greedily decide which buttons to fold: start with all visible, then
        // fold the candidate at the head of foldOrder until everything fits.
        const folded = new Set<number>();
        const widths = [...intrinsicWidths];

        const totalVisible = () => {
            let sum = 0;
            let gapCount = 0;
            for (let i = 0; i < n; i++) {
                if (!folded.has(i)) {
                    sum += widths[i];
                    gapCount++;
                }
            }
            return sum + Math.max(0, gapCount - 1) * BUTTON_GAP_PX;
        };

        for (const idx of foldOrder) {
            if (totalVisible() <= available) break;
            folded.add(idx);
        }

        // Equality-guarded set: a fresh Set object on every compute would defeat
        // React's bail-out, and since the caller rebuilds activeFlags each render
        // the recompute effect fires per render — without this guard that loop
        // becomes setState-per-frame (the infinite-flicker class of bug).
        setFoldedSet((prev) => {
            if (prev.size === folded.size && [...folded].every((i) => prev.has(i))) {
                return prev;
            }
            return folded;
        });
    }, []);

    const scheduleCompute = useCallback(() => {
        if (rafId.current !== null) return;
        rafId.current = requestAnimationFrame(() => {
            rafId.current = null;
            compute();
        });
    }, [compute]);

    const containerRef = useCallback(
        (el: HTMLDivElement | null) => {
            containerEl.current = el;
            if (el) scheduleCompute();
        },
        [scheduleCompute]
    );

    // Stable setter — same identity across renders so React ref callbacks that
    // call it won't trigger needless null → element cycles.
    const setButtonEl = useCallback((index: number, el: HTMLButtonElement | null) => {
        buttonEls.current[index] = el;
    }, []);

    // Resize the refs/cache arrays when buttonCount changes — PRESERVING
    // existing entries. Wiping them (the obvious reset) would null the refs of
    // still-mounted buttons right after React attached them, zeroing every
    // measurement until the next ref churn. compute() corrects the fold set.
    useEffect(() => {
        const resize = <T>(arr: T[], fill: T): T[] => {
            const next = arr.slice(0, buttonCount);
            while (next.length < buttonCount) next.push(fill);
            return next;
        };
        buttonEls.current = resize(buttonEls.current, null);
        widthCache.current = resize(widthCache.current, 0);
        scheduleCompute();
    }, [buttonCount, scheduleCompute]);

    // Observe container resize; re-compute when activeFlags change so an
    // active-filter promotion can trigger a re-fold.
    useEffect(() => {
        const container = containerEl.current;
        if (!container) return;
        const observer = new ResizeObserver(scheduleCompute);
        observer.observe(container);
        scheduleCompute();
        return () => {
            observer.disconnect();
            if (rafId.current !== null) {
                cancelAnimationFrame(rafId.current);
                rafId.current = null;
            }
        };
    }, [scheduleCompute]);

    // Re-compute when active state changes so the fold respects the new priority.
    useEffect(() => {
        scheduleCompute();
    }, [activeFlags, scheduleCompute]);

    return { foldedSet, containerRef, setButtonEl };
}
